import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Joyride from "react-joyride";
import type { Psychologist } from "../../../../types/psychologist";
import { SessionsWeek } from "./components/SessionsWeek";
import { SessionsTimeline } from "./components/SessionsTimeline";
import { MissingNextSessionModal } from "./components/MissingNextSessionModal";
import { SessionsJoyrideTooltip } from "./components/SessionsJoyrideTooltip";
import { useSessionsTour } from "./hooks/useSessionsTour";
import { useSessionsData, toISODate } from "./hooks/useSessionsData";
import { buildTourSessions } from "./utils/buildTourSessions";
import { useAuth } from "../../../../contexts/AuthContext";
import { getPsychologistEmail } from "../../../../hooks/api/auth/tokenManager";
import { useWidth } from "../../../../hooks/useWidth";
import type { SessionLite } from "../../../../types/session-lite";
import type { SessionWithPsychologist } from "../../../../components/psychologist/calendar/data";
import SessionDetailsModal from "../../../../components/psychologist/calendar/SessionDetailsModal";
import apiCoreAdapter from "../../../../hooks/apiCoreAdapter";
import { localTzToUTC, isoToZoneParts } from "../../../../components/psychologist/calendar/timeZoneHelpers";
import { getSessionData } from "../../../../hooks/api";
import { BA_TIMEZONE } from "../../../../utils/timezone";
import { useNoAgendamientoModal } from "../../../../components/psychologist/patients/no-agendamiento/useNoAgendamientoModal";
import { NoAgendamientoModal } from "../../../../components/psychologist/patients/no-agendamiento/NoAgendamientoModal";
import { sessionsGrade } from "../../../../components/psychologist/patients/no-agendamiento/noAgendamientoFormSchema";

import { activityTracker } from "../../../../utils/activityTracker";
interface SessionsTabProps {
  psychologist: Psychologist | null;
  onPatientClick: (patientId: string, processId?: string, patientName?: string, therapyType?: string) => void;
  onSchedule?: (patientName?: string) => void;
}

function sessionLiteToWithPsychologist(
  lite: SessionLite,
  psychologistId: string,
  therapistTz: string = BA_TIMEZONE,
): SessionWithPsychologist {
  // Extraer fecha/hora en la zona de la psicóloga (no la del navegador) para que coincida con la fila
  const parts = isoToZoneParts(lite.date, therapistTz);
  return {
    id: lite.id,
    psychologistId,
    date: parts.date,
    time: parts.time,
    modality: 'online',
    status: (lite.isCancelled ? 'cancelled' : lite.status) as SessionWithPsychologist['status'],
    patient: { name: lite.patientName, email: '', timezone: lite.patientTimezone },
    specialty: lite.therapyType,
    process_id: lite.process,
    origISO: lite.date,
  };
}

export const SessionsTab = ({ psychologist, onPatientClick, onSchedule: onScheduleProp }: SessionsTabProps) => {
  const noAgendamientoModal = useNoAgendamientoModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { size } = useWidth();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [selectedSessionLite, setSelectedSessionLite] = useState<SessionLite | null>(null);
  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);
  const isMobile = size === 's';

  const {
    sessionsLoaded,
    isLoading,
    weekDays,
    allSessions,
    sessionsForDay,
    selectedISO,
    setSelectedISO,
    onPrevWeek,
    onNextWeek,
    onToday,
    isCurrentWeek,
    refreshSessions,
    patientsMissingNext,
    patientsHandled,
    missingNextModalOpen,
    openMissingNextModal,
    dismissMissingNextModal,
  } = useSessionsData();

  const { runTour, tourKey, tourStepIndex, steps, canSkip, onTourCallback, onReplayTour } =
    useSessionsTour({ sessionsLoaded });

  // Ensure the selected day is appropriate for each tour step.
  useEffect(() => {
    if (tourStepIndex === 1) {
      const todayISO = toISODate(new Date());
      const candidate = (() => {
        for (const d of weekDays) {
          if (d.dateISO >= todayISO) return d.dateISO;
        }
        return weekDays.length ? weekDays[weekDays.length - 1].dateISO : selectedISO;
      })();
      if (candidate && candidate !== selectedISO) setSelectedISO(candidate);
    }
  }, [tourStepIndex, weekDays, selectedISO, setSelectedISO]);

  const onSchedule = (patientName?: string) => onScheduleProp?.(patientName);

  // ─── Deep-link compartible del detalle de sesión (?session=<id>) ──────────
  const openSessionDetail = (lite: SessionLite) => {
    setSelectedSessionLite(lite);
    setSessionDetailOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("session", lite.id);
      next.set("date", selectedISO); // posiciona la semana al abrir el enlace compartido
      return next;
    }, { replace: true });
  };

  const closeSessionDetail = () => {
    setSessionDetailOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("session");
      next.delete("view");
      return next;
    }, { replace: true });
  };

  // Apertura desde URL: una vez, cuando hay ?session=<id> y ya cargaron las sesiones.
  const sessionDeepLinkRef = useRef(false);
  useEffect(() => {
    if (sessionDeepLinkRef.current) return;
    const sid = searchParams.get("session");
    if (!sid) { sessionDeepLinkRef.current = true; return; }
    if (allSessions.length === 0) return; // esperar a que cargue la semana
    sessionDeepLinkRef.current = true;
    const lite = allSessions.find((s) => s.id === sid);
    if (lite) {
      setSelectedSessionLite(lite);
      setSessionDetailOpen(true);
    }
  }, [allSessions, searchParams]);

  const tourSessions = useMemo(
    () => buildTourSessions(tourStepIndex, selectedISO, sessionsForDay),
    [tourStepIndex, selectedISO, sessionsForDay],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TooltipAny = SessionsJoyrideTooltip as any;

  return (
    <section className="config-section no-padding">
      <Joyride
        key={tourKey}
        steps={steps}
        run={runTour}
        stepIndex={tourStepIndex >= 0 ? tourStepIndex : 0}
        continuous
        showSkipButton={canSkip}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        scrollOffset={400}
        hideCloseButton
        tooltipComponent={TooltipAny}
        styles={{ options: { primaryColor: '#6D57BC', zIndex: 1300 } }}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Siguiente', skip: 'Saltar' }}
        callback={onTourCallback}
      />

      <div style={{ width: isMobile ? '100%' : '80%', margin: isMobile ? '1rem auto' : '3.5rem auto', padding: isMobile ? '0 1rem' : '0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
          {/* Botón "Hoy" — navega a la semana actual y selecciona hoy */}
          <button
            id="btn-hoy-sessions"
            type="button"
            onClick={onToday}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              border: isCurrentWeek ? '2px solid #d1d5db' : '2px solid #6D57BC',
              borderRadius: '9999px',
              padding: '0.5rem 0.875rem',
              background: 'transparent',
              color: isCurrentWeek ? '#9ca3af' : '#6D57BC',
              fontWeight: 500,
              fontSize: '0.8125rem',
              height: '35px',
              cursor: isCurrentWeek ? 'default' : 'pointer',
              transition: 'all 0.2s ease-out',
            }}
          >
            Hoy
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {(patientsMissingNext.length + patientsHandled.length) > 0 && (
            <button
              type="button"
              onClick={openMissingNextModal}
              title="Ver pacientes sin próxima sesión"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                border: patientsMissingNext.length > 0 ? "2px solid #f59e0b" : "2px solid #d1d5db",
                borderRadius: "9999px",
                padding: "0.5rem 0.875rem",
                background: "transparent",
                color: patientsMissingNext.length > 0 ? "#f59e0b" : "#9ca3af",
                fontWeight: 500,
                fontSize: "0.8125rem",
                height: "35px",
                cursor: "pointer",
                transition: "all 0.2s ease-out",
              }}
            >
              Sin próxima sesión
              {patientsMissingNext.length > 0 && (
                <span style={{
                  background: "#f59e0b",
                  color: "#fff",
                  borderRadius: "9999px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  padding: "0 0.375rem",
                  lineHeight: "1.25rem",
                  minWidth: "1.25rem",
                  textAlign: "center",
                }}>
                  {patientsMissingNext.length}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onReplayTour}
            title="Repetir guía"
            aria-label="replay-sessions-onboarding"
            style={{
              display: "inline-flex",
              alignItems: "center",
              border: "2px solid #6D57BC",
              borderRadius: "9999px",
              padding: "0.5rem 0.875rem",
              background: "transparent",
              color: "#6D57BC",
              fontWeight: 500,
              fontSize: "0.8125rem",
              height: "35px",
              cursor: "pointer",
              transition: "all 0.2s ease-out",
            }}
          >
            Repetir guía
          </button>
          </div>{/* fin grupo derecha */}
        </div>

        {!sessionsLoaded ? (
          <div>
            {/* Week navigator skeleton */}
            <div className="flex items-center gap-1 mb-6 bg-white rounded-2xl p-2 border border-gray-100 shadow-sm">
              <div className="w-8 h-8 skeleton-shimmer rounded-full flex-shrink-0" />
              {[...Array(7)].map((_, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 py-1">
                  <div className="h-2.5 w-4 skeleton-shimmer rounded" />
                  <div className="w-9 h-9 skeleton-shimmer rounded-full" />
                  <div className="h-2 w-4 skeleton-shimmer rounded" />
                </div>
              ))}
              <div className="w-8 h-8 skeleton-shimmer rounded-full flex-shrink-0" />
            </div>
            {/* Session card rows */}
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-4 mb-3 bg-white rounded-xl border border-gray-100">
                <div className="w-10 h-10 skeleton-shimmer rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="h-4 skeleton-shimmer rounded w-40 mb-2" />
                  <div className="h-3 skeleton-shimmer rounded w-24" />
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <div className="w-28 h-8 skeleton-shimmer rounded-full" />
                  <div className="w-8 h-8 skeleton-shimmer rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "left" }}>
            <div id="sessions-week-selector">
              <SessionsWeek
                days={weekDays}
                onPrevWeek={onPrevWeek}
                onNextWeek={onNextWeek}
                selectedISO={selectedISO}
                onSelect={setSelectedISO}
                disabled={isLoading}
              />
            </div>

            <div id="sessions-day-timeline-container">
              {isLoading ? (
                <div className="mt-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 mb-3 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 skeleton-shimmer rounded-full flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="h-4 skeleton-shimmer rounded w-40 mb-2" />
                        <div className="h-3 skeleton-shimmer rounded w-24" />
                      </div>
                      <div className="w-28 h-8 skeleton-shimmer rounded-full flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: "1.5rem" }}>
                  <SessionsTimeline
                    sessions={tourSessions}
                    onViewSession={(session) => openSessionDetail(session)}
                    onAddNote={() => { /* TODO: Implement add note */ }}
                    onSchedule={onSchedule}
                    canSchedule={true}
                    dayISO={selectedISO}
                    therapistTz={therapistTz}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {missingNextModalOpen && (
        <MissingNextSessionModal
          patients={patientsMissingNext}
          patientsHandled={patientsHandled}
          onSchedule={onSchedule}
          onNoSchedule={(patient) => {
            const psyEmail = user?.email || getPsychologistEmail() || '';
            const total = Number.isFinite(patient.totalSessions) ? patient.totalSessions! : 0;
            noAgendamientoModal.open({
              processId: patient.processId || '',
              patientEmail: patient.email || '',
              patientName: patient.patientName || '',
              psychologistEmail: psyEmail,
              totalSessionsGrade: sessionsGrade(total),
            });
          }}
          onDismiss={dismissMissingNextModal}
          onViewPatient={(p) => {
            onPatientClick(p.patientId || '', p.processId, p.patientName);
          }}
        />
      )}

      {sessionDetailOpen && selectedSessionLite && (
        <SessionDetailsModal
          open={sessionDetailOpen}
          onClose={closeSessionDetail}
          session={sessionLiteToWithPsychologist(selectedSessionLite, psychologist?.id ?? '', therapistTz)}
          psychologistId={psychologist?.id}
          therapistTimezone={therapistTz}
          onCancel={async (sessionId, motiveId, note, initiatedBy) => {
            await apiCoreAdapter.cancelSession(sessionId, {
              motive_id: motiveId,
              note,
              initiated_by: initiatedBy,
            });
            activityTracker.trackAction("session_cancelled");
            closeSessionDetail();
            refreshSessions();
          }}
          onReschedule={async (sessionId, newDate, newTime, processId, reason) => {
            const finalProcessId = processId || selectedSessionLite.process;
            if (!finalProcessId) throw new Error('process_id no disponible');
            const timezone = therapistTz;
            await apiCoreAdapter.cancelSession(sessionId, {
              motive_id: reason?.motiveId,
              note: reason?.note ?? `Reprogramada para ${newDate} ${newTime}`,
              initiated_by: reason?.initiatedBy,
              cancellation_type: 'rescheduled',
            });
            const utcDate = localTzToUTC(newDate, newTime, timezone);
            let scheduledBy = 'platform';
            try {
              const sd = getSessionData();
              if (sd?.user?.email) scheduledBy = sd.user.email;
            } catch { /* noop */ }
            const scheduleResult = await apiCoreAdapter.scheduleSession({
              process_id: finalProcessId,
              date: utcDate.toISOString(),
              scheduled_by: scheduledBy,
            });
            // Métricas de Actividad: igual que en Agenda, solo cuenta si la sesión nueva quedó agendada
            // (scheduleSession devuelve { success: false } sin lanzar). El flujo no cambia.
            if (scheduleResult?.success !== false) activityTracker.trackAction("session_rescheduled");
            closeSessionDetail();
            refreshSessions();
          }}
          onViewProcess={() => {
            closeSessionDetail();
            const s = selectedSessionLite;
            onPatientClick(
              s.patientId || s.process,
              s.process,
              s.patientName,
              s.therapyType,
            );
          }}
        />
      )}

      <NoAgendamientoModal params={noAgendamientoModal.params} onClose={noAgendamientoModal.close} />
    </section>
  );
};
